/**
 * Centralized error reporting and logging
 * In production, this should integrate with services like Sentry
 */

export interface ErrorContext {
  userId?: string;
  userEmail?: string;
  route?: string;
  action?: string;
  metadata?: Record<string, any>;
}

export interface ErrorReport {
  message: string;
  stack?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  timestamp: string;
  url: string;
  userAgent: string;
  context?: ErrorContext;
}

class ErrorReporter {
  private isInitialized = false;
  private errorQueue: ErrorReport[] = [];
  private readonly maxQueueSize = 50;

  /**
   * Initialize error reporting
   * In production, this would initialize Sentry or similar service
   */
  initialize() {
    if (this.isInitialized) return;

    // Set up global error handlers
    window.addEventListener('error', this.handleGlobalError);
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);

    this.isInitialized = true;
    console.log('Error reporting initialized');
  }

  /**
   * Handle global JavaScript errors
   */
  private handleGlobalError = (event: ErrorEvent) => {
    this.captureError(event.error || new Error(event.message), {
      severity: 'high',
      context: {
        action: 'global_error',
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        }
      }
    });
  };

  /**
   * Handle unhandled promise rejections
   */
  private handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    this.captureError(
      event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
      {
        severity: 'high',
        context: {
          action: 'unhandled_rejection'
        }
      }
    );
  };

  /**
   * Capture and report an error
   */
  captureError(
    error: Error | string,
    options: {
      severity?: 'low' | 'medium' | 'high' | 'critical';
      context?: ErrorContext;
    } = {}
  ) {
    const errorObj = typeof error === 'string' ? new Error(error) : error;
    const severity = options.severity || 'medium';

    const report: ErrorReport = {
      message: errorObj.message,
      stack: errorObj.stack,
      severity,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      context: options.context
    };

    // Add to queue
    this.errorQueue.push(report);
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue.shift();
    }

    // Log based on severity
    if (severity === 'critical' || severity === 'high') {
      console.error('Error captured:', report);
    } else if (import.meta.env.DEV) {
      console.warn('Error captured:', report);
    }

    // In production, send to error monitoring service
    if (import.meta.env.PROD) {
      this.sendToMonitoringService(report);
    }
  }

  /**
   * Send error to monitoring service
   * TODO: Implement actual service integration (Sentry, etc.)
   */
  private async sendToMonitoringService(report: ErrorReport) {
    try {
      const dsn = import.meta.env.VITE_SENTRY_DSN;
      if (dsn && import.meta.env.PROD) {
        // @sentry/react is not a dependency yet; keep production builds working.
        console.error('Monitoring DSN is set but Sentry is not installed', report);
        return;
      }

      if (report.severity === 'critical') {
        console.error('CRITICAL ERROR:', report);
      }
    } catch (error) {
      console.error('Failed to send error to monitoring service:', error);
    }
  }

  /**
   * Capture a message (not an error)
   */
  captureMessage(
    message: string,
    level: 'info' | 'warning' | 'error' = 'info',
    context?: ErrorContext
  ) {
    if (import.meta.env.DEV) {
      console[level === 'info' ? 'log' : level === 'warning' ? 'warn' : 'error'](
        message,
        context
      );
    }

    if (import.meta.env.PROD && level === 'error') {
      // Send to monitoring service
      this.sendToMonitoringService({
        message,
        severity: 'low',
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        context
      });
    }
  }

  /**
   * Get recent errors for debugging
   */
  getRecentErrors(): ErrorReport[] {
    return [...this.errorQueue];
  }

  /**
   * Clear error queue
   */
  clearErrors() {
    this.errorQueue = [];
  }

  /**
   * Set user context for error reports
   */
  setUserContext(context: ErrorContext) {
    // Store in session for including in future error reports
    try {
      sessionStorage.setItem('error-context', JSON.stringify(context));
    } catch (e) {
      // Ignore storage errors
    }
  }

  /**
   * Clean up error reporting
   */
  cleanup() {
    window.removeEventListener('error', this.handleGlobalError);
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
    this.isInitialized = false;
  }
}

// Export singleton instance
export const errorReporter = new ErrorReporter();

// Auto-initialize in browser environment
if (typeof window !== 'undefined') {
  errorReporter.initialize();
}
